import React, { useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Stack, Button } from "@origin-digital/ods-core";
import { logger, errorForm } from "@origin-digital/reporting-client";
import { FeatureContextReact } from "@origin-digital/features";
import { useAppContext } from "../../../context/AppContext";
import offerContent from "../content.json";
import {
  APP_NAME,
  CUSTOMISATION_PATH,
  LPG_SERVICES_CUSTOMISATION_PATH,
  Routes,
} from "../../../consts";
import { submitInteractionEvent } from "../../../utils/analytics";
import {
  isFeatureEnabled,
  REDIRECT_TO_BUNDLE_CUSTOMISATION,
} from "../../../utils/feature-toggle";
import { navToUrl } from "../../../utils/navigation";
import { savePlanToCart } from "../../../services/cart-service";

export const OfferButtonSection: React.FC<{
  id: string;
  setAddressSelectorError: (error?: string) => void;
}> = ({ id, setAddressSelectorError }) => {
  const { context, setContext } = useAppContext();
  const { lookupAddress, loading, offersData } = context;
  const { push } = useHistory();
  const featureContext = useContext(FeatureContextReact);
  const customisationRedirectEnabled = isFeatureEnabled(
    featureContext,
    REDIRECT_TO_BUNDLE_CUSTOMISATION
  );

  const { addressFetching, addressRequired, primaryCta, secondaryCta } =
    offerContent;

  useEffect(() => {
    if (!loading && lookupAddress) {
      setAddressSelectorError(undefined);
    }
  }, [loading, lookupAddress, setAddressSelectorError]);

  const setAddressSelectorValidationErrorAndFocus = (error: string) => {
    setAddressSelectorError(error);

    const input = document.querySelector(
      '[data-id="connectionAddress-autocomplete-textfield-input"]'
    ) as HTMLElement;
    input?.focus();
  };

  const navigateToCallback = () => {
    if (lookupAddress && !loading) {
      const selectedPlan = offersData?.offers.find(
        (offer) => offer.salesforceId === id
      );
      setContext({
        selectedOffer: selectedPlan,
      });

      submitInteractionEvent(
        "click",
        "button",
        "request-callback-btn",
        APP_NAME,
        `Request a call back:${selectedPlan?.offerName}`
      );
      push(Routes.REQUEST_CALLBACK);
    } else if (loading) {
      setAddressSelectorValidationErrorAndFocus(addressFetching);
    } else {
      setAddressSelectorValidationErrorAndFocus(addressRequired);
    }
  };

  const navigateToSalesFormViaSelectPlan = async () => {
    if (lookupAddress && offersData?.offers && !loading) {
      const offer = offersData?.offers.find(
        ({ salesforceId }) => salesforceId === id
      );
      if (offer) {
        submitInteractionEvent(
          "click",
          "button",
          "select-offer-btn",
          APP_NAME,
          `Select offer:${offer.offerName}`
        );
        const originAddressId = lookupAddress.originAddressId;
        await savePlanToCart(
          originAddressId!,
          TAL.config.cartServiceEndPoint,
          offer
        );
        logger.debug("[navigateToSelectPlanViaSalesForm] OriginAddressId", {
          originAddressId,
        });
        const redirectPath = customisationRedirectEnabled
          ? CUSTOMISATION_PATH
          : LPG_SERVICES_CUSTOMISATION_PATH;

        navToUrl(
          `${redirectPath}?originAddressId=${originAddressId}&useCart=true`
        );
      }
    } else if (loading) {
      setAddressSelectorValidationErrorAndFocus(addressFetching);
    } else {
      errorForm({
        appName: APP_NAME,
        formName: "lpg-offer-card",
        errorMessage: addressRequired,
        errorCode: "SAP error",
      });
      setAddressSelectorValidationErrorAndFocus(addressRequired);
    }
  };

  return (
    <Stack space="medium">
      <Button
        data-id="select-offer-btn"
        onClick={navigateToSalesFormViaSelectPlan}
        variant="contained"
        fullWidth
      >
        {primaryCta}
      </Button>
      <Button
        data-id="request-callback-btn"
        variant="outlined"
        onClick={navigateToCallback}
        fullWidth
      >
        {secondaryCta}
      </Button>
    </Stack>
  );
};
